import dayjs from "dayjs";
import { ApiFileRespAWT } from "@/shared/interfaces/erp/drive-files/drive-files.interface";
import { FotoTrasladoEliminada, esFotoEliminada } from "./traslado/fotos-traslado.utils";

export interface FotoVisitaItem {
  photoUrl: string | null;
  photoId: string | null;
  photoname: string;
  fecha_hora: string; // ISO — fecha/hora de carga
}

/**
 * Reglas de fotos de la VISITA (mismas cantidades que los modales del técnico):
 *  - Ingreso: 2 fotos (al iniciar la visita).
 *  - Proceso: entre 2 y 5 fotos, en cualquier momento.
 *  - Finalización: entre 2 y 5 fotos (al finalizar), en `urls_fotos_salida.photosVisita`.
 */
export const FOTOS_INGRESO_VISITA = 2;
export const FOTOS_PROCESO_MIN_VISITA = 2;
export const FOTOS_PROCESO_MAX_VISITA = 5;
export const FOTOS_FINALIZACION_VISITA = 2;

export type EtapaFotoVisita = "INGRESO" | "PROCESO" | "FINALIZACION";

/**
 * Sube un array de File a MinIO (via handleUploadFile) y devuelve el array de
 * items {photoUrl, photoId, photoname, fecha_hora} para persistir en la OT.
 */
export async function subirFotosVisita(
  files: File[],
  path: string,
  handleUploadFile: (f: File | null, p: string) => Promise<ApiFileRespAWT>,
  prefijo = "Foto"
): Promise<FotoVisitaItem[]> {
  const results = await Promise.all(files.map((f) => handleUploadFile(f, path)));
  return results.map((r, i) => ({
    photoUrl: r?.url || null,
    photoId: r?.id_archivos || r?.url || null,
    photoname: `${prefijo} ${i + 1}`,
    fecha_hora: dayjs().toISOString(),
  }));
}

/** Extrae las URLs (no nulas) de un array de fotos guardado en la OT. */
export function fotosVisitaUrls(arr: any): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((f) => f?.photoUrl).map((f) => f.photoUrl as string);
}

// ---------------------------------------------------------------------------
// Fotos eliminadas por Activaciones (mismo modelo de MARCAS que el traslado).
//
// Cuando Activaciones elimina una foto, el backend la sustituye por una marca
// (`eliminada: true`, sin `photoUrl`, con quién y cuándo). Esa marca es lo que
// indica que al técnico le falta volver a subir una foto.
//  - Ingreso / proceso: la marca ocupa el lugar de la foto dentro del arreglo.
//  - Finalización: las marcas van en `urls_fotos_salida.eliminadas`.
// ---------------------------------------------------------------------------

/** Detecta si una OT corresponde al flujo de VISITA. */
export function esOrdenVisita(ordenTrabajo: any): boolean {
  const V = "VISITA";
  return (
    ordenTrabajo?.agendamiento_data?.tipo_orden_servicio === V ||
    ordenTrabajo?.agendamiento?.tipo_orden_servicio === V ||
    ordenTrabajo?.tipo_orden_trabajo === V
  );
}

/** Arreglo en su orden original: fotos y, en su lugar, las marcas de eliminadas. */
function itemsDe(arr: any): any[] {
  return Array.isArray(arr) ? arr.filter((f: any) => f?.photoUrl || esFotoEliminada(f)) : [];
}

export function itemsIngresoVisitaDe(ordenTrabajo: any): any[] {
  return itemsDe(ordenTrabajo?.urls_fotos_ingreso);
}

export function itemsProcesoVisitaDe(ordenTrabajo: any): any[] {
  return itemsDe(ordenTrabajo?.urls_fotos_proceso);
}

/** Fotos de ingreso cargadas (sin marcas). */
export function fotosIngresoVisitaDe(ordenTrabajo: any): any[] {
  return itemsIngresoVisitaDe(ordenTrabajo).filter((f) => f?.photoUrl);
}

/** Fotos de proceso cargadas (sin marcas). */
export function fotosProcesoVisitaDe(ordenTrabajo: any): any[] {
  return itemsProcesoVisitaDe(ordenTrabajo).filter((f) => f?.photoUrl);
}

/** Fotos de finalización: se guardan como { photosVisita: { fotoEvidenciaN } }. */
export function fotosFinalizacionVisitaDe(ordenTrabajo: any): any[] {
  const obj = ordenTrabajo?.urls_fotos_salida?.photosVisita;
  if (!obj || typeof obj !== "object") return [];
  return (Object.values(obj) as any[]).filter((f: any) => f?.photoUrl);
}

/** Marcas de fotos de ingreso eliminadas que el técnico aún no repone. */
export function eliminadasIngresoVisitaDe(ordenTrabajo: any): FotoTrasladoEliminada[] {
  return itemsIngresoVisitaDe(ordenTrabajo).filter(esFotoEliminada);
}

/** Marcas de fotos de proceso eliminadas que el técnico aún no repone. */
export function eliminadasProcesoVisitaDe(ordenTrabajo: any): FotoTrasladoEliminada[] {
  return itemsProcesoVisitaDe(ordenTrabajo).filter(esFotoEliminada);
}

/** Marcas de fotos de finalización eliminadas que el técnico aún no repone. */
export function eliminadasFinalizacionVisitaDe(ordenTrabajo: any): FotoTrasladoEliminada[] {
  const salida = ordenTrabajo?.urls_fotos_salida;
  const marcas = salida?.eliminadas;
  if (!Array.isArray(marcas)) return [];
  const photos = salida?.photosVisita && typeof salida.photosVisita === "object" ? salida.photosVisita : {};
  return marcas.filter((m: any) => m?.clave && !photos[m.clave]?.photoUrl);
}

/** El técnico ya inició la visita (subió las fotos de ingreso). */
export function fotosVisitaIniciada(ordenTrabajo: any): boolean {
  const v = ordenTrabajo?.fecha_hora_inicio_visita;
  return !!v && `${v}`.toLowerCase() !== "null" && `${v}`.toLowerCase() !== "undefined";
}

/** El técnico ya finalizó la visita (OT REALIZADA). */
export function fotosVisitaFinalizada(ordenTrabajo: any): boolean {
  const estado = `${ordenTrabajo?.estado_orden_trabajo || ""}`.toUpperCase();
  return !!ordenTrabajo?.fecha_hora_fin_visita || estado.startsWith("REALIZADA");
}

export interface ResumenFotosVisita {
  ingreso: number;
  proceso: number;
  finalizacion: number;
  iniciado: boolean;
  finalizado: boolean;
  /** Fotos de ingreso que Activaciones eliminó y falta volver a subir. */
  faltanIngreso: number;
  /** Fotos de proceso que Activaciones eliminó y falta volver a subir. */
  faltanProceso: number;
  /** Fotos de finalización que Activaciones eliminó y falta volver a subir. */
  faltanFinalizacion: number;
  /** Hay fotos eliminadas por Activaciones pendientes de volver a subir. */
  incompleto: boolean;
}

/**
 * Estado de la evidencia de la visita. Lo que "falta" son las fotos que
 * Activaciones eliminó (marcas) y el técnico todavía no repone.
 */
export function resumenFotosVisita(ordenTrabajo: any): ResumenFotosVisita {
  const faltanIngreso = eliminadasIngresoVisitaDe(ordenTrabajo).length;
  const faltanProceso = eliminadasProcesoVisitaDe(ordenTrabajo).length;
  const faltanFinalizacion = eliminadasFinalizacionVisitaDe(ordenTrabajo).length;
  return {
    ingreso: fotosIngresoVisitaDe(ordenTrabajo).length,
    proceso: fotosProcesoVisitaDe(ordenTrabajo).length,
    finalizacion: fotosFinalizacionVisitaDe(ordenTrabajo).length,
    iniciado: fotosVisitaIniciada(ordenTrabajo),
    finalizado: fotosVisitaFinalizada(ordenTrabajo),
    faltanIngreso,
    faltanProceso,
    faltanFinalizacion,
    incompleto: faltanIngreso > 0 || faltanProceso > 0 || faltanFinalizacion > 0,
  };
}

/** Texto corto de lo que falta volver a subir ("1 de ingreso, 2 de proceso"). */
export function textoFotosFaltantesVisita(r: ResumenFotosVisita): string {
  const partes: string[] = [];
  if (r.faltanIngreso > 0) partes.push(`${r.faltanIngreso} de ingreso`);
  if (r.faltanProceso > 0) partes.push(`${r.faltanProceso} de proceso`);
  if (r.faltanFinalizacion > 0) partes.push(`${r.faltanFinalizacion} de finalización`);
  return partes.join(", ");
}
